import { inject, Injectable } from '@angular/core';
import { CanActivate, GuardResult, MaybeAsync } from '@angular/router';
import { FeatureFlagsHttpClient } from '@core/services/http/feature-flags-http-client';
import { KeycloakClient } from '@core/services/keycloak/keycloak.client';
import { AuthActions } from '@core/store/auth/auth.actions';
import { FeatureFlagActions } from '@core/store/feature-flags/feature-flags.actions';
import { LoggedInUser } from '@libs/shared/interfaces/logged-in-user.interface';
import { Store } from '@ngrx/store';
import { KeycloakProfile } from 'keycloak-js';
import { combineLatest, from, map, tap } from 'rxjs';

/**
 * This guard is used to initialize the state of the application. Id prefer to do this with a global resolver, but sadly resolvers are run
 * after the guards @see https://angular.dev/api/router/Resolve?tab=usage-notes
 * So we have to use a guard for this task instead because we need to have the state initialized before other guards are run.
 */

type KeycloakProfileWithAttributes = KeycloakProfile & {
  attributes?: {
    company: string[];
  };
};
@Injectable({
  providedIn: 'root',
})
export class RequiredStateResolver implements CanActivate {
  private readonly keycloakClient = inject(KeycloakClient);
  private readonly featureFlagsClient = inject(FeatureFlagsHttpClient);
  private readonly store = inject(Store);

  canActivate(): MaybeAsync<GuardResult> {
    return combineLatest([
      from(this.keycloakClient.getKeycloakUser()),
      this.featureFlagsClient.getFeatureFlags(),
    ]).pipe(
      tap(([user, featureFlags]) => {
        const transformedUser = this.transformKeycloakProfileToUser(
          user as KeycloakProfileWithAttributes,
        );
        this.store.dispatch(
          AuthActions.setLoggedInUser({
            loggedInUser: transformedUser,
          }),
        );
        this.store.dispatch(
          FeatureFlagActions.setAllFeatureFlags({ featureFlags }),
        );
      }),
      map(() => true), // Return true to allow the navigation
    );
  }

  private transformKeycloakProfileToUser(
    keycloakUserProfile: KeycloakProfileWithAttributes,
  ) {
    const userRoles = this.keycloakClient.getUserRoles();

    const loggedInUser: LoggedInUser = {
      id: keycloakUserProfile.id ?? '',
      email: keycloakUserProfile.email ?? '',
      firstName: keycloakUserProfile.firstName ?? '',
      lastName: keycloakUserProfile.lastName ?? '',
      username: `${keycloakUserProfile.firstName} ${keycloakUserProfile.lastName}`,
      roles: userRoles,
      company: keycloakUserProfile.attributes?.company[0] ?? 'unknown',
    };
    return loggedInUser;
  }
}
